/**
* @render react
* @name Button
*/

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import './_style.scss';

/**
 * Button component
 *
 * @param {Object} props - the props object
 * @param {string} props.type - The type of the button. Can be submit/reset/button
 * @param {string} props.label - The label of the button
 * @param {boolean} props.disabled - Is button disabled?
 * @param {Node} props.children - Components to be added as a child to the Link
 * @param {Function} props.onClick - onClick callback
 * @param {string} props.variant - adding additional class to the button elements, which determinats background colour of the button.
 *                          In case empty string is passed, listed classes won't be added
 * @param {string} props.srLabel - Screen Reader label to be applied when the label is not present
 * @param {boolean} props.loadStyles - Whether to apply the class names for styling.
 * @param {string} props.ampOn - String for AMP event
 * @param {Object} props.extraClassNames - Extra class names to be applied to the component
 * @param {Object} props.context - Extra information coming from Polaris provider.
 */
const Button = (
  {
    type,
    label,
    disabled,
    children,
    onClick,
    variant,
    srLabel,
    loadStyles,
    ampOn,
    extraClassNames,
    context: { cssPrefix, platform },
  },
) => (
  /* eslint-disable react/button-has-type */
  /* https://github.com/yannickcr/eslint-plugin-react/issues/1555 */
  <button
    type={type}
    className={classNames({
      [`${cssPrefix}__button`]: loadStyles,
      [`${variant}`]: true,
      ...extraClassNames,
    })}
    onClick={onClick}
    disabled={disabled}
    aria-label={srLabel}
    title={srLabel}
    on={platform === 'amp' ? ampOn : undefined}
  >
    {label}
    {children}
  </button>
);

/**
 * Valid props
 */
Button.propTypes = {
  label: PropTypes.string,
  type: PropTypes.oneOf(['submit', 'reset', 'button']),
  disabled: PropTypes.bool,
  children: PropTypes.node,
  onClick: PropTypes.func,
  variant: PropTypes.string,
  srLabel: PropTypes.string,
  ampOn: PropTypes.string,
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.shape(),
  context: PropTypes.shape(),
};

/**
 * Default props
 */
Button.defaultProps = {
  label: '',
  type: 'button',
  disabled: false,
  children: [],
  onClick: () => {},
  variant: '-primary',
  srLabel: 'Button',
  loadStyles: true,
  ampOn: null,
  extraClassNames: {},
  context: {},
};

export default withPolaris(Button);
